import { KnockProvider } from "@knocklabs/react";
import { environmentConfig } from "@src/appV2/environment";
import { knock } from "@src/appV2/environment/config";
import { type ReactNode } from "react";

import {
  KnockGuideProviderWithRefetch,
  type KnockGuideProviderWithRefetchProps,
} from "./KnockGuideProviderWithRefetch";

interface KnockProviderWrapperProps {
  children: ReactNode;
  userId: string | undefined;
  knockToken: string | undefined;
  onUserTokenExpiring: () => Promise<string | undefined>;
  targetParams?: KnockGuideProviderWithRefetchProps["targetParams"];
}

/**
 * Wraps the Knock React SDK providers (`KnockProvider` → `KnockGuideProvider`)
 * around the authenticated app tree. Renders children without providers when the
 * Knock token is unavailable (e.g. pre-auth, admin impersonation).
 *
 * Adapted from `cbh-mobile-app`'s `KnockProviderWrapper`. We skip `KnockFeedProvider`
 * for now since the admin frontend doesn't yet have an in-app notification feed — only
 * guides are needed for the initial launch (rebrand announcement blast).
 */
export function KnockProviderWrapper(props: KnockProviderWrapperProps) {
  const { children, userId, knockToken, onUserTokenExpiring, targetParams } = props;

  if (!knockToken) {
    return children;
  }

  return (
    <KnockProvider
      apiKey={environmentConfig.REACT_APP_KNOCK_PUBLIC_API_KEY}
      user={{ id: userId }}
      userToken={knockToken}
      onUserTokenExpiring={onUserTokenExpiring}
    >
      <KnockGuideProviderWithRefetch
        readyToTarget
        channelId={knock.channels.guides}
        targetParams={targetParams}
      >
        {children}
      </KnockGuideProviderWithRefetch>
    </KnockProvider>
  );
}
